import { Download, ExternalLink, X } from "lucide-react";
import { useState } from "react";

export const DEFAULT_SUPPORT_GREETING = {
  id: "support-greeting",
  sender_role: "admin",
  sender_name: "Dental OS",
  body: "Hi! How can we help you today?",
  created_at: new Date().toISOString(),
  is_own_message: false,
};

export default function SupportThread({
  messages = [],
  greeting = false,
  greetingMessage = DEFAULT_SUPPORT_GREETING,
  compact = false,
}) {
  const [preview, setPreview] = useState(null);
  const rows = messages.length > 0 ? messages : (greeting ? [greetingMessage] : []);
  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
        No tickets yet. Send us a message or open a ticket.
      </div>
    );
  }

  return (
    <div className={["space-y-4", compact ? "px-3 py-4" : "px-6 py-6"].join(" ")}>
      {rows.map((message) => {
        if (message.message_type === "system") {
          return (
            <div key={message.id} className="text-center">
              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {message.body}
              </span>
            </div>
          );
        }
        const own = message.is_own_message || message.sender_role === "clinic";
        const avatarLabel = own ? "" : getSenderAvatarLabel(message);
        const attachment = message.attachment_url ? {
          url: message.attachment_url,
          name: message.attachment_name || "Attachment",
          mime: message.attachment_mime_type || "",
        } : null;
        return (
          <div key={message.id} className={`flex items-start gap-2 ${own ? "justify-end" : "justify-start"}`}>
            {!own ? (
              <div
                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-700 text-xs font-semibold text-white shadow-sm"
                aria-hidden="true"
              >
                {avatarLabel}
              </div>
            ) : null}
            <div className={["max-w-[82%]", compact ? "text-sm" : "text-sm"].join(" ")}>
              <div className={[
                "rounded-2xl px-3.5 py-2.5 shadow-sm",
                own
                  ? "rounded-br-md bg-teal-700 text-white"
                  : "rounded-bl-md border border-slate-200 bg-white text-slate-950",
              ].join(" ")}>
                <div className="whitespace-pre-wrap break-words">{message.body}</div>
              </div>
              {attachment ? <AttachmentPreviewCard attachment={attachment} onPreview={setPreview} /> : null}
              <div className={`mt-1 text-[11px] text-slate-500 ${own ? "text-right" : "text-left"}`}>
                {!own ? `${message.sender_name || "Dental OS"} · ` : ""}
                {formatMessageTime(message.created_at)}
              </div>
            </div>
          </div>
        );
      })}
      <AttachmentPreviewModal attachment={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

function AttachmentPreviewCard({ attachment, onPreview }) {
  return (
    <div className="relative mt-2 inline-flex max-w-full cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
      <button
        type="button"
        onClick={() => onPreview(attachment)}
        className="absolute inset-0 rounded-lg"
        aria-label={`Preview ${attachment.name}`}
      />
      {attachment.mime.startsWith("image/") ? (
        <img src={attachment.url} alt="" className="pointer-events-none relative h-9 w-9 rounded object-cover" />
      ) : null}
      <span className="pointer-events-none relative truncate">{attachment.name}</span>
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className="relative rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        aria-label={`Download ${attachment.name}`}
      >
        <Download className="h-3.5 w-3.5 shrink-0" />
      </a>
    </div>
  );
}

function AttachmentPreviewModal({ attachment, onClose }) {
  if (!attachment) return null;
  const isImage = attachment.mime.startsWith("image/");
  const isPdf = attachment.mime === "application/pdf";
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/80 p-4" role="dialog" aria-modal="true" aria-label={attachment.name}>
      <button type="button" className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Close preview backdrop" />
      <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0 truncate text-sm font-semibold text-slate-900">{attachment.name}</div>
          <div className="flex shrink-0 items-center gap-2">
            <a href={attachment.url} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label={`Open ${attachment.name}`}>
              <ExternalLink className="h-4 w-4" />
            </a>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close preview">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-100 p-3">
          {isImage ? (
            <img src={attachment.url} alt={attachment.name} className="max-h-[78vh] max-w-full rounded object-contain" />
          ) : isPdf ? (
            <iframe src={attachment.url} title={attachment.name} className="h-[78vh] w-full rounded border border-slate-200 bg-white" />
          ) : (
            <a href={attachment.url} target="_blank" rel="noreferrer" className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white">
              Open attachment
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function formatMessageTime(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "";
  }
}

function getSenderAvatarLabel(message) {
  const explicit = message.sender_avatar_label || message.sender_avatar || "";
  const fallback = message.sender_name || "Dental OS";
  return String(explicit || fallback).trim().slice(0, 1).toUpperCase() || "D";
}
